import { useEffect, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';

import Button, { ButtonStyleType } from '@/components/common/Button/Button';
// eslint-disable-next-line import/no-named-as-default
import ValidationAlert from '@/components/common/ValidationAlert/ValidationAlert';
import { GetRenewalSettings } from '@/graphql/contracts/_generated_/getRenewalSettings.query';
import { UpdateRenewalSettings, UpdateRenewalSettingsVariables } from
  '@/graphql/contracts/_generated_/updateRenewalSettings.mutation';
import { Fields, validationSchema } from '../validation/schema';
import Input from '@/components/common/Input/Input';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import { NumberUtils } from '@/utils/numbers.utils';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';
import { EnvUtils } from '@/utils/env.utils';

type InitialRenewalSettings = {
  [key in keyof UpdateRenewalSettingsVariables]: number;
};

interface IBillingAndRenewalsFormProps {
  onSubmit: (values: UpdateRenewalSettingsVariables) => Promise<UpdateRenewalSettings | null>;
  renewalSettings: GetRenewalSettings['renewalSettings'];
}

export default function BillingAndRenewalsForm({ onSubmit, renewalSettings }: IBillingAndRenewalsFormProps) {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  useEffect(() => {
    const getInitialData = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };

    getInitialData();
  }, [getUserRole]);

  const canModifyGlobalSettings = useMemo(() => {
    return EnvUtils.isAlterGlobalSettingsButtonEnabled() &&
      userRole === UserRole.FULL_ACCESS;
  }, [userRole]);

  const initValues: InitialRenewalSettings = useMemo(() => {
    return {
      Monthly: renewalSettings.find((el) => el.billingInterval === 'Monthly')?.renewalDays ?? 0,
      Annual: renewalSettings.find((el) => el.billingInterval === 'Annual')?.renewalDays ?? 0,
      Quarterly: renewalSettings.find((el) => el.billingInterval === 'Quarterly')?.renewalDays ?? 0,
      HalfYearly: renewalSettings.find((el) => el.billingInterval === 'HalfYearly')?.renewalDays ?? 0,
    };
  }, [renewalSettings]);

  return (
    <Formik<Fields>
      initialValues={initValues}
      validationSchema={validationSchema}
      onSubmit={() => setShowConfirmationModal(true)}
      validateOnMount={true}
      validateOnChange={true}
    >
      {({ values, isValid, dirty, errors, touched, setFieldTouched, setFieldValue, resetForm }) => (
        <Form>
          <AgConfirmationModal
            isOpen={showConfirmationModal}
            title="You are about to modify Days Before Renewal for all contracts in C&B"
            onClose={() => setShowConfirmationModal(false)}
            onConfirm={() => {
              onSubmit({
                Annual: NumberUtils.parseIntegerInput(values.Annual as string),
                HalfYearly: NumberUtils.parseIntegerInput(values.HalfYearly as string),
                Monthly: NumberUtils.parseIntegerInput(values.Monthly as string),
                Quarterly: NumberUtils.parseIntegerInput(values.Quarterly as string)
              }).then(data => data ? resetForm({ values }) : resetForm());

            }}
            contentStyle={{ width: 650 }}
          >
            You are about to modify Days Before Renewal for all contracts in C&B. <br />
            This change could potentially impact Sales Order generation. <br />
            Please confirm you wish to continue.
          </AgConfirmationModal>
          <div className="row d-flex align-items-center mb-20">
            <div className="col-2">
              <label htmlFor="renewal-global-settings-monthly">
                Monthly:
              </label>
            </div>
            <div className="col">
              <Input
                min={0}
                step={1}
                type="number"
                disabled={!canModifyGlobalSettings}
                id="renewal-global-settings-monthly"
                style={{ width: 100, textAlign: 'right' }}
                value={values.Monthly}
                onChange={(e) => {
                  setFieldTouched('Monthly', true, false);
                  setFieldValue('Monthly', e.target.value);
                }}
              />

              {errors.Monthly && touched.Monthly && (
                <ValidationAlert isFloating={false}>
                  {errors.Monthly}
                </ValidationAlert>
              )}
            </div>

          </div>

          <div className="row d-flex align-items-center mb-20">
            <div className="col-2">
              <label htmlFor="renewal-global-settings-quarterly">
                Quarterly:
              </label>
            </div>
            <div className="col">
              <Input
                min={0}
                step={1}
                type="number"
                disabled={!canModifyGlobalSettings}
                id="renewal-global-settings-quarterly"
                style={{ width: 100, textAlign: 'right' }}
                value={values.Quarterly}
                onChange={(e) => {
                  setFieldTouched('Quarterly', true, false);
                  setFieldValue('Quarterly', e.target.value);
                }}
              />

              {errors.Quarterly && touched.Quarterly && (
                <ValidationAlert isFloating={false}>
                  {errors.Quarterly}
                </ValidationAlert>
              )}
            </div>
          </div>

          <div className="row d-flex align-items-center mb-20">
            <div className="col-2">
              <label htmlFor="renewal-global-settings-half-yearly">
                Half-Yearly:
              </label>
            </div>
            <div className="col">
              <Input
                disabled={!canModifyGlobalSettings}
                min={0}
                step={1}
                type="number"
                id="renewal-global-settings-half-yearly"
                style={{ width: 100, textAlign: 'right' }}
                value={values.HalfYearly}
                onChange={(e) => {
                  setFieldTouched('HalfYearly', true, false);
                  setFieldValue('HalfYearly', e.target.value);
                }}
              />

              {errors.HalfYearly && touched.HalfYearly && (
                <ValidationAlert isFloating={false}>
                  {errors.HalfYearly}
                </ValidationAlert>
              )}
            </div>
          </div>

          <div className="row d-flex align-items-center mb-40">
            <div className="col-2">
              <label htmlFor="renewal-global-settings-yearly">
                Yearly:
              </label>
            </div>
            <div className="col">
              <Input
                min={0}
                step={1}
                disabled={!canModifyGlobalSettings}
                type="number"
                id="renewal-global-settings-yearly"
                style={{ width: 100, textAlign: 'right' }}
                value={values.Annual}
                onChange={(e) => {
                  setFieldTouched('Annual', true, false);
                  setFieldValue('Annual', e.target.value);
                }}
              />

              {errors.Annual && touched.Annual && (
                <ValidationAlert isFloating={false}>
                  {errors.Annual}
                </ValidationAlert>
              )}
            </div>
          </div>

          {canModifyGlobalSettings && (
            <div>
              <Button
                type="submit"
                buttonStyleType={ButtonStyleType.PRIMARY}
                disabled={!isValid || !dirty}
              >
                Save
              </Button>
            </div>
          )}
        </Form>
      )}

    </Formik>
  );
}
